import { useState } from 'react';
import './Calculators.css';

const DEAUVILLE_SCORES = [
  {
    score: 1,
    label: 'No uptake above background',
    color: '#15803d',
  },
  {
    score: 2,
    label: 'Uptake ≤ mediastinum',
    color: '#15803d',
  },
  {
    score: 3,
    label: 'Uptake > mediastinum but ≤ liver',
    color: '#22c55e',
  },
  {
    score: 4,
    label: 'Uptake moderately > liver',
    color: '#a16207',
  },
  {
    score: 5,
    label: 'Uptake markedly > liver and/or new lesions',
    color: '#dc2626',
  },
];

const BASELINE_OPTIONS = [
  { value: 'reduced', label: 'Reduced vs baseline' },
  { value: 'unchanged', label: 'Unchanged vs baseline' },
  { value: 'increased', label: 'Increased vs baseline / new lesions' },
];

// Lugano response categories (Cheson 2014) applied to the 5-point Deauville scale (Barrington 2014).
// DS 1-3 = complete metabolic response at either timepoint, but the action differs: at interim the
// patient continues planned therapy, only at end of treatment does CMR mean surveillance.
// DS 4-5 is never automatically progressive disease — it depends on the timepoint and on the change
// in uptake relative to the baseline scan. Increased uptake or new lesions = PMD at either timepoint.
function interpret(score, isInterim, baseline) {
  if (score <= 3) {
    return {
      responseLabel: 'Complete Metabolic Response (CMR)',
      color: '#15803d',
      tone: 'not-recommended',
      advice: isInterim
        ? 'Deauville 1-3 at interim = complete metabolic response. Continue planned therapy — an interim CMR is not an indication to stop treatment or move to surveillance.'
        : 'Deauville 1-3 at end of treatment = complete metabolic response. Proceed to surveillance.',
    };
  }

  if (baseline === 'increased') {
    return {
      responseLabel: 'Progressive Metabolic Disease (PMD)',
      color: '#dc2626',
      tone: 'recommended',
      advice:
        'Increased uptake compared with baseline, or new FDG-avid lesions, = progressive metabolic disease. Confirm with biopsy where feasible and change therapy / consider salvage.',
    };
  }

  if (isInterim) {
    if (baseline === 'unchanged') {
      return {
        responseLabel: 'No Metabolic Response (stable disease)',
        color: '#c2410c',
        tone: 'amber',
        advice:
          'Deauville 4-5 with uptake unchanged from baseline at interim = no metabolic response. Re-evaluate the treatment plan; consider biopsy of the PET-avid site before switching therapy.',
      };
    }
    return {
      responseLabel: 'Partial Metabolic Response (PMR)',
      color: '#a16207',
      tone: 'amber',
      advice:
        'Deauville 4-5 with reduced uptake vs baseline at interim = partial metabolic response — the disease is responding. Continue therapy, or apply the response-adapted escalation/de-escalation rule for the regimen. Salvage is not indicated on this result alone.',
    };
  }

  return {
    responseLabel: 'Residual Metabolic Disease',
    color: '#c2410c',
    tone: 'amber',
    advice:
      'Deauville 4-5 at end of treatment = residual metabolic disease. Biopsy the residual PET-avid site to confirm viable lymphoma before committing to salvage therapy.',
  };
}

function DeauvilleCalculator() {
  const [selected, setSelected] = useState(null);
  const [isInterim, setIsInterim] = useState(false);
  const [baseline, setBaseline] = useState('reduced');

  const entry = selected !== null ? DEAUVILLE_SCORES[selected] : null;
  const result = entry ? interpret(entry.score, isInterim, baseline) : null;

  return (
    <div className="calculator">
      <div className="calc-header">
        <h3>Deauville Score</h3>
        <span className="calc-subtitle">PET/CT Response Assessment (Lugano)</span>
      </div>

      <div className="calc-toggle-row">
        <label className="calc-toggle">
          <input
            type="radio"
            name="timing"
            checked={!isInterim}
            onChange={() => setIsInterim(false)}
          />
          End-of-treatment
        </label>
        <label className="calc-toggle">
          <input
            type="radio"
            name="timing"
            checked={isInterim}
            onChange={() => setIsInterim(true)}
          />
          Interim
        </label>
      </div>

      <div className="deauville-options">
        {DEAUVILLE_SCORES.map((d, i) => (
          <button
            key={d.score}
            className={`deauville-option ${selected === i ? 'selected' : ''}`}
            style={selected === i ? { borderColor: d.color, background: d.color + '12' } : {}}
            onClick={() => setSelected(i)}
          >
            <span className="deauville-score" style={{ color: d.color }}>
              {d.score}
            </span>
            <span className="deauville-desc">{d.label}</span>
          </button>
        ))}
      </div>

      {entry && entry.score >= 4 && (
        <div className="calc-factor-group">
          <div className="calc-factor-label-bold">Uptake compared with baseline scan</div>
          <div className="calc-toggle-row">
            {BASELINE_OPTIONS.map((o) => (
              <label key={o.value} className="calc-toggle">
                <input
                  type="radio"
                  name="baseline"
                  checked={baseline === o.value}
                  onChange={() => setBaseline(o.value)}
                />
                {o.label}
              </label>
            ))}
          </div>
        </div>
      )}

      {entry && (
        <div className="calc-results">
          <div className="calc-risk-row">
            <span className="calc-risk-label">
              {isInterim ? 'Interim' : 'End-of-treatment'} Assessment:
            </span>
            <span className="calc-risk-badge" style={{ background: entry.color }}>
              Deauville {entry.score}
            </span>
          </div>
          <div className="calc-risk-row">
            <span className="calc-risk-label">Interpretation:</span>
            <span style={{ fontWeight: 500, color: result.color }}>{result.responseLabel}</span>
          </div>
          <div
            className={`calc-prophylaxis${result.tone === 'amber' ? '' : ` ${result.tone}`}`}
            style={result.tone === 'amber' ? { background: '#fef3c7', borderColor: '#a16207' } : undefined}
          >
            {result.advice}
          </div>
        </div>
      )}

      <div className="calc-reference">
        Barrington SF, Mikhaeel NG, Kostakoglu L, et al. Role of imaging in the staging and response assessment of lymphoma. <em>J Clin Oncol.</em> 2014;32(27):3048-3058. <a href="https://pubmed.ncbi.nlm.nih.gov/25113771/" target="_blank" rel="noopener noreferrer">PMID 25113771</a> (5-point scale). Response categories: Cheson BD, Fisher RI, Barrington SF, et al. Recommendations for initial evaluation, staging, and response assessment of Hodgkin and non-Hodgkin lymphoma: the Lugano classification. <em>J Clin Oncol.</em> 2014;32(27):3059-3068. <a href="https://pubmed.ncbi.nlm.nih.gov/25113753/" target="_blank" rel="noopener noreferrer">PMID 25113753</a>
      </div>
    </div>
  );
}

export default DeauvilleCalculator;
